import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "store/store";
import { RootState } from "store/reducers";
import { SubscriptionWidget } from "components/widgets/SubscriptionWidget";
import { CloudAssetsWidget } from "components/widgets/CloudAssetsWidget";
import { VideoUsageWidget } from "components/widgets/VideoUsageWidget";
import { VideoLibraryWidget } from "components/widgets/VideoLibraryWidget";
import { CatalogWidget } from "components/widgets/CatalogWidget";
import { ScheduledLivestreamsWidget } from "components/widgets/ScheduledLivestreamsWidget";
import { useTranslation } from "react-i18next";
import { EntitlementWidget } from "components/widgets/EntitlementWidget";
import { StripeStatus } from "../platforms/types";
import { useClaimCheck } from "hooks/useClaimCheck";
import { VideoPlayerWidget } from "components/widgets/VideoPlayerWidget";
import { ReferralParticipantWidget } from "components/widgets/ReferralParticipantWidget";
import { ShopifyLinkedWidget } from "components/widgets/ShopifyLinkedWidget";
import { AnalyticsWidget } from "components/widgets/AnalyticsWidget";
import { usePageHeader } from "hooks/usePageHeader";
import { Row } from "../../../components/layout/Row";
import { PlayerActivationTrackerBanner } from "components/banners/progress-tracker-banners/PlayerActivationTrackerBanner";
import { useSwitcherSdk } from "hooks/useSwitcherSdk";
import { useHasAccess } from "hooks/useHasAccess";
import { useCatalogData } from "hooks/useCatalogData";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { postEvents } from "store/events/thunks";
import { DynamicActionBanner } from "components/banners/DynamicActionBanner";
import { useHasPlanRole } from "hooks/useHasPlanRole";
import { PlatformsWidget } from "components/widgets/PlatformsWidget";
import { TutorialPlayerEmbeds } from "components/tutorial-player-embeds";

export const HomePage: React.FC = () => {
    const { t } = useTranslation();
    const { isInBrowser } = useSwitcherSdk();
    const dispatch = useDispatch<AppDispatch>();
    const user = useSelector((state: RootState) => state.user);
    const { events } = useSelector((state: RootState) => state.events);

    const [allStepsCompleted, setAllStepsCompleted] = useState<boolean>(false);
    const [hasActiveSub, setHasActiveSub] = useState<boolean>(false);
    const [hasCloudAssets, setHasCloudAssets] = useState(false);
    const [hasVideoAccess, setHasVideoAccess] = useState(false);
    const hasVideoPlayerAccess = useClaimCheck("videoplayer");
    const hasGatedContentAccess = useClaimCheck("gatedcontent");
    const hasShopifyAccess = useClaimCheck("shopify");
    const videoPlayerDisabled = useClaimCheck("videoplayer.disabled");
    const isStudioUser = useHasPlanRole("Studio");
    const isMerchantUser = useHasPlanRole("Merchant");
    const isBusinessUser = useHasPlanRole("Business");
    const hasAppClaim = useClaimCheck("app");
    const hasAccess = useHasAccess();
    const userHasFirstName: boolean =
        user?.userInfo?.User?.Details?.FirstName !== "";

    //This is due to the business role not being used currently because business users needed shopify access. This is just in case we need to use that role.
    const hasActivationTrackerRoles = useMemo(() => {
        return isBusinessUser || isMerchantUser;
    }, [isBusinessUser, isMerchantUser]);

    const {
        hasCatalogClaim,
        loading,
        catalogData,
        embedCode: catalogEmbedCode,
        shareLink
    } = useCatalogData({
        projectId: user?.userInfo?.MetaData?.Details?.ProjectId
    });

    // Get User Entitlements
    const { data: entitlements } = useSwitcherClient(
        (client) => client.userEntitlements_getActiveUserEntitlements,
        {
            requestImmediately: true,
            args: [user.userInfo?.User?.Details?.Id]
        }
    );

    // Fetch usage data
    const { data: usage, loading: videoLibraryDataLoading } = useSwitcherClient(
        (client) => client.cloudRecordings_GetUsage,
        {
            requestImmediately: true,
            onSuccess: (data) => {
                dispatch(
                    postEvents({
                        "added-video-to-library": data?.TotalRecordings > 0
                    })
                );
            }
        }
    );

    // Stripe Customer
    const { data: stripeCustomer } = useSwitcherClient(
        (client) => client.stripe_GetCustomer,
        {
            requestImmediately: true,
            onSuccess: (data) => {
                const hasActiveSub = data?.StripeSubscriptions.some(
                    (s) =>
                        s.Status === StripeStatus.Active &&
                        s.PauseCollection !== null
                );
                setHasActiveSub(hasActiveSub);
            }
        }
    );

    // Assets usage
    const { data: assetsUsage, loading: assetsUsageLoading } =
        useSwitcherClient(
            (client) => client.userAssetsUsage_GetUserAssetsUsage,
            {
                requestImmediately: true,
                args: [user.userInfo?.User?.Details?.Id]
            }
        );

    // Twilio usage
    const { data: twilioUsage, loading: twilioUsageLoading } =
        useSwitcherClient((client) => client.twilio_GetRoomUsage, {
            requestImmediately: hasAccess,
            args: [user.userInfo?.User?.Details?.Id]
        });

    // Shopify store credentials
    const { data: shopifyStoreCredentials, loading: storeCredentialsLoading } =
        useSwitcherClient((client) => client.shopify_GetCredentials, {
            requestImmediately: true,
            args: []
        });

    usePageHeader({
        title: userHasFirstName
            ? t("home:welcome", {
                  name: user?.userInfo?.User?.Details?.FirstName
              })
            : t("home:nameless-welcome")
    });

    useEffect(() => {
        if (
            user.userInfo !== undefined &&
            user.userInfo?.User?.Details?.Id !== undefined
        ) {
            setHasCloudAssets(
                user.userInfo?.Claims?.Details?.FeatureClaims?.indexOf(
                    "cloudassets"
                ) !== -1
            );
            setHasVideoAccess(
                user.userInfo?.Claims?.Details?.FeatureClaims?.indexOf(
                    "videochat"
                ) !== -1
            );
        }
    }, [user.userInfo]);

    const shouldShowActivationTracker = useMemo(() => {
        return (
            !allStepsCompleted &&
            hasActivationTrackerRoles &&
            isInBrowser &&
            !videoPlayerDisabled &&
            hasGatedContentAccess
        );
    }, [
        allStepsCompleted,
        hasActivationTrackerRoles,
        isInBrowser,
        videoPlayerDisabled,
        hasGatedContentAccess
    ]);

    const widgetToShow = useMemo(() => {
        if (hasActiveSub) {
            return <SubscriptionWidget customer={stripeCustomer ?? {}} />;
        } else if (entitlements?.length > 0) {
            return <EntitlementWidget entitlements={entitlements} />;
        } else {
            return null;
        }
    }, [hasActiveSub, stripeCustomer, entitlements]);

    const cmsRow = useMemo(
        () =>
            !videoPlayerDisabled &&
            hasVideoPlayerAccess && (
                <Row header={t("home:manage-content")}>
                    <VideoLibraryWidget
                        loading={videoLibraryDataLoading}
                        cloudUsage={usage}
                    />
                    {hasCatalogClaim ? (
                        <CatalogWidget
                            enabled={hasCatalogClaim}
                            loading={loading}
                            catalogId={catalogData?.Details?.Id}
                            shareLink={shareLink}
                        />
                    ) : (
                        <VideoPlayerWidget
                            enabled={hasVideoPlayerAccess}
                            hasActiveSub={hasActiveSub}
                        />
                    )}
                    {events?.["added-video-to-library"] && <AnalyticsWidget />}
                </Row>
            ),
        [
            videoPlayerDisabled,
            hasVideoPlayerAccess,
            hasCatalogClaim,
            loading,
            catalogData,
            shareLink,
            videoLibraryDataLoading,
            usage,
            events,
            hasActiveSub,
            t
        ]
    );

    const creationRow = useMemo(
        () =>
            hasAppClaim && (
                <Row header={t("home:creation-tools")}>
                    <PlatformsWidget />
                    <ScheduledLivestreamsWidget />
                    <CloudAssetsWidget
                        enabled={hasCloudAssets}
                        assets={assetsUsage ?? {}}
                        loading={assetsUsageLoading}
                    />
                    <VideoUsageWidget
                        userId={user.userInfo?.User?.Details?.Id}
                        usage={twilioUsage ?? {}}
                        enabled={hasVideoAccess}
                        loading={twilioUsageLoading}
                    />
                </Row>
            ),
        [
            hasAppClaim,
            hasCloudAssets,
            assetsUsage,
            assetsUsageLoading,
            user.userInfo?.User?.Details?.Id,
            twilioUsage,
            hasVideoAccess,
            twilioUsageLoading,
            t
        ]
    );

    const cmsAndCreationRows = useMemo(() => {
        return isStudioUser ? (
            // If the user is a studio user, show Creation widgets first
            <>
                {creationRow}
                {cmsRow}
            </>
        ) : (
            // Otherwise, show CMS widgets first
            <>
                {cmsRow}
                {creationRow}
            </>
        );
    }, [cmsRow, creationRow, isStudioUser]);

    return (
        <>
            {shouldShowActivationTracker ? (
                <PlayerActivationTrackerBanner
                    hasCatalogClaim={hasCatalogClaim}
                    catalogEmbedCode={catalogEmbedCode}
                    videoLibraryDataLoading={videoLibraryDataLoading}
                    onAllStepsCompleted={() => setAllStepsCompleted(true)}
                />
            ) : (
                <DynamicActionBanner />
            )}
            {cmsAndCreationRows}
            <Row header={t("home:account-overview")}>
                {widgetToShow}
                {import.meta.env.VITE_REFERRAL_ENABLE_REFERRALS === "true" &&
                    !user.userInfo.Claims?.Details?.Roles?.includes(
                        "Affiliate"
                    ) && <ReferralParticipantWidget />}
                {hasShopifyAccess && (
                    <ShopifyLinkedWidget
                        loading={storeCredentialsLoading}
                        enabled={shopifyStoreCredentials?.length > 0}
                        storeCredentials={shopifyStoreCredentials ?? []}
                    />
                )}
            </Row>

            {hasAppClaim && <TutorialPlayerEmbeds />}
        </>
    );
};
